/** Explicit validators for deployed configuration records; no registry or
 * created-row defaults. Serialized JSON is inspected, never repaired here. */
import type { z } from "zod";
import { apiKeyRecord } from "../schemas/apiKeys";
import { catalogItemRecord, eventTypeInput, leadSourceInput, venueInput } from "../schemas/catalog";
import { memberPatch } from "../schemas/members";
import { companySettingsPatch, organizationPatch } from "../schemas/settings";
import { noteRecord } from "../schemas/notes";
import { evaluation, zodIssues, type EntityType, type ValidationAction, type ValidationIssue } from "./issues";

type Row = Record<string, unknown>;
export interface AdminFacts {
  before?: Row;
  changedFields?: readonly string[];
  /** False only after a company/venue-authorized lookup; never pass hidden rows. */
  references?: Record<string, boolean>;
  self?: boolean;
  checksComplete?: boolean;
}
const unchanged = (record: Row, facts: AdminFacts, fields: string[]) => Boolean(facts.before && facts.changedFields && fields.every(field => !facts.changedFields!.includes(field) && Object.is(facts.before![field], record[field])));

function inspectedJson(record: Row, fields: string[]) {
  const inspected = { ...record };
  for (const field of fields) if (typeof inspected[field] === "string") {
    try { inspected[field] = JSON.parse(inspected[field] as string); } catch { /* Keep invalid raw evidence for the schema finding. */ }
  }
  return inspected;
}
function schemaFindings(record: Row, facts: AdminFacts, schema: z.ZodType, type: EntityType, inspected = record): ValidationIssue[] {
  const entity = { type, ...(typeof record.id === "string" ? { id: record.id } : typeof record.companyId === "string" ? { id: record.companyId } : {}) };
  const action: ValidationAction = `${type}.save`;
  const result = schema.safeParse(inspected);
  const issues = result.success ? [] : zodIssues(result.error.issues, entity, action).map(issue => ({ ...issue, code: `${type}.${issue.fields[0] || "record"}_invalid`, blocks: unchanged(record, facts, issue.fields.map(field => field.split(".")[0])) ? [] : [action] }));
  for (const [field, exists] of Object.entries(facts.references ?? {})) if (exists === false && record[field]) issues.push({ code: `${type}.reference_missing`, kind: "error", entity, fields: [field], message: "The referenced record is unavailable. Review this field.", blocks: unchanged(record, facts, [field]) ? [] : [action] });
  return issues;
}

export function validateVenue(record: Row, facts: AdminFacts = {}) {
  return evaluation(schemaFindings(record, facts, venueInput, "venue", inspectedJson(record, ["aliases"])), facts.checksComplete ?? false);
}
export function validateEventType(record: Row, facts: AdminFacts = {}) {
  return evaluation(schemaFindings(record, facts, eventTypeInput, "event_type"), facts.checksComplete ?? false);
}
export function validateLeadSource(record: Row, facts: AdminFacts = {}) {
  return evaluation(schemaFindings(record, facts, leadSourceInput, "lead_source"), facts.checksComplete ?? false);
}
export function validateCatalog(record: Row, facts: AdminFacts = {}) {
  const issues = schemaFindings(record, facts, catalogItemRecord, "catalog", inspectedJson(record, ["dayRates"]));
  if (record.pricingAction && record.autoAddWithPackage) issues.push({ code: "catalog.action_auto_add", kind: "error", entity: { type: "catalog", ...(typeof record.id === "string" ? { id: record.id } : {}) }, fields: ["pricingAction", "autoAddWithPackage"], message: "Pricing actions must be chosen manually, not auto-added", blocks: unchanged(record, facts, ["pricingAction", "autoAddWithPackage"]) ? [] : ["catalog.save"] });
  return evaluation(issues, facts.checksComplete ?? false);
}
export function validateOrganization(record: Row, facts: AdminFacts = {}) {
  return evaluation(schemaFindings(record, facts, organizationPatch, "settings"), facts.checksComplete ?? false);
}
export function validateCompanySettings(record: Row, facts: AdminFacts = {}) {
  return evaluation(schemaFindings(record, facts, companySettingsPatch, "settings", inspectedJson(record, ["damageNoticeDays", "damageTeamWarnDays", "insuranceReminderDays", "balanceReminderDays", "overdueReminderDays", "notifyEmails", "notifyPhones"])), facts.checksComplete ?? false);
}
export function validateMember(record: Row, facts: AdminFacts = {}) {
  const issues = schemaFindings(record, facts, memberPatch, "member");
  if (facts.self && record.active === 0 && facts.changedFields?.includes("active")) issues.push({ code: "member.self_deactivate", kind: "error", entity: { type: "member", ...(typeof record.id === "string" ? { id: record.id } : {}) }, fields: ["active"], message: "you cannot deactivate yourself", blocks: ["member.save"] });
  return evaluation(issues, facts.checksComplete ?? false);
}
export function validateNote(record: Row, facts: AdminFacts = {}) {
  return evaluation(schemaFindings(record, facts, noteRecord, "note"), facts.checksComplete ?? false);
}
export function validateApiKey(record: Row, facts: AdminFacts = {}) {
  return evaluation(schemaFindings(record, facts, apiKeyRecord, "api_key", inspectedJson(record, ["scopes"])), facts.checksComplete ?? false);
}
